/**
 * (c) 2022, Nous Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { Box, BoxProps, HStack, Stack, Text, VStack } from "@chakra-ui/layout";
import {
    Collapse,
    Tooltip,
    useClipboard,
    useDisclosure,
    VisuallyHidden,
} from "@chakra-ui/react";
import { default as React, ReactNode, useCallback, useMemo } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { pythonSnippetMediaType } from "../../common/mediaTypes";
import { zIndexCode } from "../../common/zIndex";
import { useActiveEditorActions } from "../../editor/active-editor-hooks";
import { PasteContext } from "../../editor/codemirror/copypaste";
import {
    debug as dndDebug,
    DragContext,
    setDragContext,
} from "../../editor/codemirror/dnd";
import {
    DocSectionsSplit,
    splitDocString,
} from "../../editor/codemirror/language-server/docstrings";
import {
    ApiDocsBaseClass,
    ApiDocsEntry,
    ApiDocsFunctionParameter,
} from "../../language-server/apidocs";
import { useLogging } from "../../logging/logging-hooks";
import { Anchor } from "../../router-hooks";
import { useSessionSettings } from "../../settings/session-settings";
import CodeActionButton from "../common/CodeActionButton";
import DocString from "../common/DocString";
import DragHandle from "../common/DragHandle";
import ShowMoreButton from "../common/ShowMoreButton";
import { allowWrapAtPeriods } from "../common/wrap";
import { useCodeDragImage } from "../documentation-hooks";
import Highlight from "../reference/Highlight";

const kindToFontSize: Record<string, any> = {
    module: "2xl",
    class: "lg",
};

const kindToHeading: Record<string, any> = {
    module: "h2",
    class: "h3",
    variable: "h4",
    function: "h4",
};

const kindToSpacing: Record<string, any> = {
    module: 5,
    class: 5,
    variable: 4,
    function: 4,
};

interface ApiDocEntryNodeProps extends BoxProps {
    docs: ApiDocsEntry;
    anchor?: Anchor;
    parentType?: string;
}

const ApiNode = ({
    anchor,
    docs,
    parentType,
    ...props
}: ApiDocEntryNodeProps) => {
    const { id, kind } = docs;
    // Numeric suffixes are used for overrides but links may omit them when
    // a specific override is not known and we should match the first only.
    const active = anchor && (anchor.id === id || anchor.id + "-1" === id);
    const disclosure = useDisclosure();
    return (
        <Highlight anchor={anchor} active={active} id={id} disclosure={disclosure}>
            <Stack
                wordBreak="break-word"
                fontSize="sm"
                spacing={3}
                // Reduce padding inside a class.
                pt={kindToSpacing[kind] - (parentType === "class" ? 1 : 0)}
                pb={kindToSpacing[kind] - (parentType === "class" ? 2 : 1)}
                pl={5}
                pr={0}
                mt={1}
                mb={1}
                {...props}
            >
                <ApiNodeSelf
                    docs={docs}
                    showMore={disclosure.isOpen}
                    onToggleShowMore={disclosure.onToggle}
                />
                <ApiNodeChildren docs={docs} anchor={anchor} />
            </Stack>
        </Highlight>
    );
};

interface ApiNodeSelfProps {
    docs: ApiDocsEntry;
    showMore: boolean;
    onToggleShowMore: () => void;
}

/**
 * The current node's details, not its children.
 */
const ApiNodeSelf = ({
    docs,
    showMore,
    onToggleShowMore,
}: ApiNodeSelfProps) => {
    const { name, fullName, kind, params, baseClasses, docString } = docs;
    const { signature, hasSignatureDetail } = buildSignature(
        kind,
        params,
        showMore
    );
    const docParts = useMemo(
        () =>
            docString ? splitDocString(docString) : ({} as Partial<DocSectionsSplit>),
        [docString]
    );
    const hasExample = docParts.example && docParts.example.length > 0;
    const hasRemainder = docParts.remainder && docParts.remainder.length > 0;
    const hasShowMoreContent = hasRemainder || hasExample;

    const spacing = 3;
    return (
        <VStack alignItems="stretch" spacing={spacing} pr={3}>
            {kind === "function" || kind === "variable" ? (
                <DraggableSignature
                    signature={signature}
                    docs={docs}
                    alignSelf="flex-start"
                />

            ) : (
                    <Text
                        fontFamily="code"
                        fontSize={kindToFontSize[kind] || "md"}
                        as={kindToHeading[kind]}
                    >
                        <Text as="span">{formatName(kind, fullName, name)}</Text>
                        {signature}
                    </Text>
                )}

            {baseClasses && baseClasses.length > 0 && (
                <BaseClasses value={baseClasses} />
            )}
            {docParts.summary && (
                <DocString fontWeight="normal" value={docParts.summary ?? ""} />
            )}
            {(hasShowMoreContent || hasSignatureDetail) && (
                <>
                    <ShowMoreButton onClick={onToggleShowMore} isOpen={showMore} />
                    {hasShowMoreContent && (
                        // Avoid VStack spacing here so the margin animates too.
                        <Collapse in={showMore} style={{ marginTop: 0 }}>
                            <VStack spacing={spacing} mt={3} alignItems="stretch">
                                {hasRemainder && (
                                    <DocString
                                        fontWeight="normal"
                                        value={docParts.remainder!}
                                        mt={3}
                                    />
                                )}
                                {hasExample && (
                                    <Box className="docs-code">
                                        Example: <code>{docParts.example}</code>
                                    </Box>
                                )}
                            </VStack>
                        </Collapse>
                    )}
                </>
            )}
        </VStack>
    );
};

interface ApiNodeChildrenProps {
    anchor: Anchor | undefined;
    docs: ApiDocsEntry;
}

const ApiNodeChildren = ({ docs, anchor }: ApiNodeChildrenProps) => {
    const { kind, children } = docs;
    const intl = useIntl();
    const groupedChildren = useMemo(() => {
        const filteredChildren = filterChildren(children);
        return filteredChildren
            ? groupBy(filteredChildren, (c) => c.kind)
            : undefined;
    }, [children]);

    return groupedChildren && groupedChildren.size > 0 ? (
        <Box pl={kind === "class" ? 2 : 0} mt={3}>
            <Box
                pl={kind === "class" ? 2 : 0}
                borderLeftWidth={kind === "class" ? 1 : undefined}
            >
                {["function", "variable", "class"].map(
                    (childKind) =>
                        groupedChildren?.get(childKind as any) && (
                            <Box mb={5} key={childKind}>
                                <Text fontWeight="lg" mb={2}>
                                    {groupHeading(intl, kind, childKind)}
                                </Text>
                                {groupedChildren?.get(childKind as any)?.map((c) => (
                                    <ApiNode
                                        anchor={anchor}
                                        key={c.id}
                                        docs={c}
                                        parentType={docs.kind}
                                    />
                                ))}
                            </Box>
                        )
                )}
            </Box>
        </Box>
    ) : null;
};

const groupHeading = (
    intl: IntlShape,
    kind: string,
    childKind: string
): string => {
    switch (childKind) {
        case "variable":
            return intl.formatMessage({ id: "apidocs-fields" });
        case "class":
            return intl.formatMessage({ id: "apidocs-classes" });
        case "function":
            return intl.formatMessage({
                id: kind === "class" ? "apidocs-methods" : "apidocs-functions",
            });
        default: {
            throw new Error("Unexpected");
        }
    }
};

const formatName = (kind: string, fullName: string, name: string): string => {
    return kind === "module" ? allowWrapAtPeriods(fullName) : name;
};

const buildSignature = (
    kind: string,
    params: ApiDocsFunctionParameter[] | undefined,
    detailed: boolean
): { signature?: string; hasSignatureDetail: boolean } => {
    if (kind === "function" && params) {
        const signature =
            "(" +
            params
                .filter(
                    (parameter, index) =>
                        // This happens for * separating positional and keyword-only arguments.
                        // For now we always omit it.
                        parameter.name &&
                        // Self parameter
                        !(index === 0 && parameter.name === "self") &&
                        (detailed || parameter.defaultValue === undefined)
                )
                .map((parameter) => {
                    const prefix =
                        parameter.category === "varargDict"
                            ? "**"
                            : parameter.category === "varargList"
                                ? "*"
                                : "";
                    const suffix = parameter.defaultValue
                        ? `=${parameter.defaultValue}`
                        : "";
                    return prefix + parameter.name + suffix;
                })
                .join(", ") +
            ")";
        return {
            signature,
            hasSignatureDetail: !!params.find((p) => p.defaultValue !== undefined),
        };
    }
    return { signature: undefined, hasSignatureDetail: false };
};

const isInitOrOtherNonDunderMethod = (c: ApiDocsEntry) =>
    !c.name.endsWith("__") || c.name === "__init__";

const filterChildren = (
    children: ApiDocsEntry[] | undefined
): ApiDocsEntry[] | undefined =>
    children ? children.filter(isInitOrOtherNonDunderMethod) : undefined;
// const filterChildren = (
//     children: ApiDocsEntry[] | undefined
// ): ApiDocsEntry[] | undefined =>
//     children
//         ? children.filter(entry => {
//             console.log(entry); // 打印 entry 的值
//             return isInitOrOtherNonDunderMethod(entry) && !entry.name.includes("event.gamepad_event_ZL_");
//         })
//         : undefined;


function groupBy<T, U>(values: T[], fn: (x: T) => U): Map<U, T[]> {
    const result = new Map<U, T[]>();
    for (const v of values) {
        const k = fn(v);
        let array = result.get(k);
        if (!array) {
            array = [];
            result.set(k, array);
        }
        array.push(v);
    }
    return result;
}

const BaseClasses = ({ value }: { value: ApiDocsBaseClass[] }) => {
    return (
        <Text pl={2}>
            <FormattedMessage
                id="apidocs-baseclass"
                values={{ baseClassCount: value.length }}
            />{" "}
            {value.map((bc) => (
                <a key={bc.fullName} href={"#" + bc.fullName}>
                    {bc.name}
                </a>
            ))}
        </Text>
    );
};

const classToInstanceMap: Record<string, string> = {
    Button: "button_a",
    MicroBitDigitalPin: "pin0",
    MicroBitTouchPin: "pin0",
    MicroBitAnalogDigitalPin: "pin0",
    Image: "Image.HEART",
    uname_result: "uname()",
};

const getDragPasteData = (fullName: string, kind: string): PasteContext => {
    // console.log("传入的fullName", fullName)
    let parts = fullName.split(".").filter((p) => p !== "__init__");
    // Heuristic identification of e.g. Image.HEART. Sufficient for MicroPython API.
    if (!parts[parts.length - 1].match(/^[A-Z0-9_]+$/)) {
        parts = parts.map((p) => classToInstanceMap[p] ?? p);
    }
    const isMicrobit = parts[0] === "microbit";
    const nameAsWeImportIt = isMicrobit ? parts.slice(1) : parts;
    let code = nameAsWeImportIt.join(".") + (kind === "function" ? "()" : "");
    const irControlRegex = /ir_control_(\w+)_pressed$/;

    const extensions = ['servo', 'lego_motor', 'dc_motor', 'techable_machine', 'joystick', 'led_strip', 'rotary_angle_sensor', 'temperature_humidity_sensor', 'servo360', 'pins', 'ports']

    //  这个对event单独处理
    // console.log(parts[0], 99999)
    if (parts[0] === 'event') {
        switch (parts[1]) {
            case 'start':
                code = '@event.' + parts[1] + '\ndef on_event_start():\n    pass';
                break;
            case 'button_triangle_pressed':
                code = '@event.' + parts[1] + '\ndef on_event_triangle():\n    pass';
                break;
            case 'button_square_pressed':
                code = '@event.' + parts[1] + '\ndef on_event_square():\n    pass';
                break;
            case 'button_circular_pressed':
                code = '@event.' + parts[1] + '\ndef on_event_circular():\n    pass';
                break;
            case 'obstacle_distance_more_than':
                code = '@event.' + parts[1] + '()\ndef on_obstacle_distance_more_than():\n    pass';
                break;
            case 'obstacle_distance_less_than':
                code = '@event.' + parts[1] + '()\ndef on_obstacle_distance_less_than():\n    pass';
                break;
            case 'loudness_more_than':
                code = '@event.' + parts[1] + '()\ndef on_loudness_more_than():\n    pass';
                break;
            case 'loudness_less_than':
                code = '@event.' + parts[1] + '()\ndef on_loudness_less_than():\n    pass';
                break;
            case 'left_light_more_than':
                code = '@event.' + parts[1] + '()\ndef on_left_light_more_than():\n    pass';
                break;
            case 'left_light_less_than':
                code = '@event.' + parts[1] + '()\ndef on_left_light_less_than():\n    pass';
                break;
            case 'right_light_more_than':
                code = '@event.' + parts[1] + '()\ndef on_right_light_more_than():\n    pass';
                break;
            case 'right_light_less_than':
                code = '@event.' + parts[1] + '()\ndef on_right_light_less_than():\n    pass';
                break;
            case 'color_detected':
                code = '@event.' + parts[1] + '()\ndef on_color_detected():\n    pass';
                break;
            case 'message_received':
                code = '@event.' + parts[1] + '()\ndef on_message_received():\n    pass';
                break;
        }
        if (irControlRegex.test(parts[1])) {
            const word = parts[1].replace(irControlRegex, '$1');
            // console.log('word', word)
            code = '@event.' + parts[1] + `()\ndef on_ir_event_${word}():\n    pass`;
            parts[0] = 'event';
        }
    }  else if (parts[0] === 'led_matrix' && parts[1] === 'show_image') {
        code = "#example:\n#led_matrix.show_image(bytearray([0x00,0x00,0x00,0x00,0x38,0x1c,0x7c,0x3e,0x44,0x22,0x00,0x00,0x00,0x00,0x00,0x00]), 2)\n" + code
    } else if (parts[0] === 'sensor') {
        if (parts[1] === 'get_tof_distance' || parts[1] === 'is_obstacle_distance') {
            code = code.replace('sensor.', 'tof.');
        } else if (parts[1] === 'is_sound_value') {
            code = code.replace('sensor.', 'mic.');
        } else if (parts[1] === 'is_pressed') {
            code = code.replace('sensor.', 'button.');
        } else if (parts[1] === 'get_timer' || parts[1] === 'reset_timer') {
            code = code.replace('sensor.', 'timer.');
        }
    } else if (parts[0] === 'IoT') {
        if (parts[1] === 'cloud_message') {
            code = '@event.' + parts[1] + '()\ndef on_cloud_mesh1_message():\n    pass';
            parts[0] = 'event';
        } else if (parts[1] === 'connect_wifi' || parts[1] === 'isconnected_wifi' || parts[1] === 'disconnect_wifi') {
            code = code.replace('IoT.', 'wifi.');
        }
    } else if (parts[0] === 'techable_machine') {
        // techable_machine模块
        code = code.replace('techable_machine.', 'tf.');
        // }
    } else if (parts[0] === 'infrared' ) {
        if (irControlRegex.test(parts[1])){
            const word = parts[1].replace(irControlRegex, '$1');
            code = '@event.' + parts[1] + `\ndef on_ir_event_${word}():\n    pass`;
            parts[0] = 'event';
        }else {
            code = code.replace('infrared.', 'sensor.');
        }
    } else if (parts[0] === 'protocol_v1') {
        // console.log(parts[1]);
        if (parts[1].startsWith("gamepad_")) {
            let extractedPart = parts[1].split("gamepad_")[1];
            let functionName = `on_gamepad_event_${extractedPart}`;
            code = `@event.${parts[1]}\ndef ${functionName}():\n    pass`;
            parts[0] = 'event';
        } else if (parts[1].startsWith("on_gamepad_")) {
            let extractedPart = parts[1].split("on_gamepad_event_")[1].split('_')[0]
            let functionName = `gamepad_${extractedPart}_rocker_pressed`;
            code = `@event.${functionName}()\ndef ${parts[1]}():\n    pass`;
            parts[0] = 'event';

        }
    } else if (parts[0] === "control") {
        if (parts[1] === "sleep") {
            code = code.replace("control.", "time.");
        } else if (parts[1] === "stop") {
            code = code.replace("control.", "script.");
        }
    } else if (parts[0] === "NousRobot") {
        if (parts[1] === "dance" || parts[1] === "emotion" || parts[1] === "behavior") {
            code = code.replace("NousRobot.", "action.");
        } else {
            code = code.replace("NousRobot.", "motion.");
        }
    } else if (parts[0] === "screen") {
        if (parts[1] === "fill_screen" || parts[1] === "draw_text" || parts[1] === "draw_line" || parts[1] === "draw_rectangle" || parts[1] === "draw_round_rectangle" || parts[1] === "draw_circle" || parts[1] === "turn_on" || parts[1] === "clear_screen" || parts[1] === "draw_line") {
            code = code.replace("screen.", "lcd.");
        }
    } else if (parts[0] === "sound") {
        code = code.replace("sound.", "audio.");
    } else if (parts[0] === "music") {
        code = code.replace("music.", "audio.");
    } else if (parts[0] === "vision") {
        if (parts[1] === "led_ctrl") {
            // code = code.replace("vision.", "matatalab.");
        }
    }

    // console.log("extensions.includes(parts[1]", extensions.includes(parts[1]), parts[1])
    let requiredImport = isMicrobit
        ? `from microbit import *`
        : `import ${parts[0]}`;
    if (extensions.includes(parts[1])) {
        // const word = parts[0].replace('extention.', '');
        requiredImport = `from extention import ${parts[1]}`;
        code = code.replace('extention.', '');
    }
    // 如果是内置模块不需要import
    const builtinsModule = ['leds', 'led_matrix', 'event', 'audio', 'action', 'motion', 'sensor', 'control', 'infrared', 'techable_machine', 'IoT', 'vision', 'lcd', 'protocol_v1', 'event_motor_module'];
    const full = builtinsModule.includes(parts[0]) ? code : `${requiredImport}\n${code}`;
    return {
        code,
        codeWithImports: full,
        type: kind === "function" ? "call" : "example",
        id: `api-${fullName}`,
    };
};

// 解析代码
const getPasteContext = (fullName: string, kind: string): PasteContext => {
    return getDragPasteData(fullName, kind);
};

export const getDragContext = (fullName: string, kind: string): DragContext => {
    const { codeWithImports: code, type, id } = getDragPasteData(fullName, kind);
    return {
        code,
        type,
        id,
    };
};

interface DraggableSignatureProps extends BoxProps {
    signature: ReactNode;
    docs: ApiDocsEntry;
}

const DraggableSignature = ({
    signature,
    docs,
    ...props
}: DraggableSignatureProps) => {
    const { fullName, kind, name, id } = docs;
    const logging = useLogging();
    const dragImage = useCodeDragImage();
    const handleDragStart = useCallback(
        (event: React.DragEvent) => {
            logging.event({
                type: "code-drag",
                message: `api-${id}`,
            });
            dndDebug("dragstart");
            event.dataTransfer.dropEffect = "copy";
            const context = getDragContext(fullName, kind);
            setDragContext(context);
            event.dataTransfer.setData(pythonSnippetMediaType, context.code);
            if (dragImage.current) {
                event.dataTransfer.setDragImage(dragImage.current, 0, 0);
            }
        },
        [fullName, kind, id, dragImage, logging]
    );

    const handleDragEnd = useCallback((event: React.DragEvent) => {
        dndDebug("dragend");
        setDragContext(undefined);
    }, []);

    const highlight = useDisclosure();
    const copyCodeButton = useDisclosure();
    const actions = useActiveEditorActions();

    const { code, codeWithImports, type } = getPasteContext(fullName, kind);
    const { onCopy } = useClipboard(code);
    const handleCopyCode = useCallback(async () => {
        onCopy();
        await actions?.copyCode(code, codeWithImports, type, id);
    }, [actions, code, codeWithImports, onCopy, type, id]);
    const isMac = /Mac/.test(navigator.platform);
    const handleKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleCopyCode();
            }
            if ((e.key === "c" || e.key === "C") && (isMac ? e.metaKey : e.ctrlKey)) {
                e.preventDefault();
                handleCopyCode();
            }
        },
        [handleCopyCode, isMac]
    );
    const intl = useIntl();
    const [{ dragDropSuccess }] = useSessionSettings();
    return (
        <Box position="relative">
            <Tooltip
                hasArrow
                placement="top-start"
                label={intl.formatMessage({ id: "drag-hover" })}
                closeOnClick={false}
                isDisabled={dragDropSuccess}
            >
                <HStack
                    draggable
                    spacing={0}
                    onClick={copyCodeButton.onToggle}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    display="inline-flex"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="blimpTeal.300"
                    borderRadius="lg"
                    onMouseEnter={highlight.onOpen}
                    onMouseLeave={highlight.onClose}
                    tabIndex={0}
                    position="relative"
                    zIndex={zIndexCode}
                    _focusVisible={{
                        boxShadow: "var(--chakra-shadows-outline);",
                        outline: "none",
                    }}
                    onKeyDown={handleKeyDown}
                    {...props}
                    cursor="grab"
                >
                    <VisuallyHidden>
                        <FormattedMessage id="code-example" />
                    </VisuallyHidden>
                    <DragHandle
                        highlight={highlight.isOpen}
                        borderTopLeftRadius="lg"
                        borderBottomLeftRadius="lg"
                        p={1}
                        alignSelf="stretch"
                    />
                    <Text
                        minW={40}
                        background={highlight.isOpen ? "blimpTeal.50" : "white"}
                        transition="background .2s"
                        p={2}
                        fontFamily="code"
                        fontSize={kindToFontSize[kind] || "md"}
                        as={kindToHeading[kind]}
                    >
                        <Text as="span">{formatName(kind, fullName, name)}</Text>
                        {signature}
                    </Text>
                </HStack>
            </Tooltip>
            <CodeActionButton
                isOpen={copyCodeButton.isOpen}
                toHighlighted={highlight.onOpen}
                toDefault={highlight.onClose}
                codeAction={handleCopyCode}
                borderAdjustment={false}
                toolkitType="api"
            />
        </Box>
    );
};

export default ApiNode;
